import { CheckCircle2 } from 'lucide-react';
import { SolutionUseCaseCard } from '@/types/scenarios/solution';

interface SolutionUseCasesShowcaseProps {
  title?: string;
  subtitle?: string;
  useCases: SolutionUseCaseCard[];
}

const UseCaseCard = ({ useCase }: { useCase: SolutionUseCaseCard }) => {
  return (
    <div className="group bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
      {/* Image */}
      <div className="relative h-48 sm:h-56 overflow-hidden">
        <img
          src={useCase.image}
          alt={useCase.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
          {useCase.title}
        </h3>

        <p className="text-gray-600 text-base mb-5">
          {useCase.description}
        </p>

        <div className="space-y-2.5">
          {useCase.bullets.map((bullet, index) => (
            <div key={index} className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700 text-sm sm:text-base">{bullet}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const SolutionUseCasesShowcase = ({
  title = "Stop Losing Business",
  subtitle = "Every missed call is a missed opportunity. See how businesses like yours are capturing revenue around the clock.",
  useCases,
}: SolutionUseCasesShowcaseProps) => {
  if (!useCases || useCases.length === 0) {
    return null;
  }

  return (
    <section className="w-full bg-gray-50 py-16 md:py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* 2x2 Grid of Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {useCases.map((useCase) => (
            <UseCaseCard key={useCase.id} useCase={useCase} />
          ))}
        </div>
      </div>
    </section>
  );
};
